import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Plus, Download, Trash2 } from 'lucide-react';
import EncaissementForm from './EncaissementForm';

const sourceTypes = [
  { value: 'own_funds', label: 'Fonds propres' },
  { value: 'partner_contribution', label: 'Participation partenaire' },
  { value: 'bank_loan', label: 'Financement bancaire' },
  { value: 'public_subsidy', label: 'Subvention publique' },
  { value: 'leasing', label: 'Leasing financier' }
];

const statusOptions = [
  { value: 'draft_preparation', label: 'Brouillon' },
  { value: 'submitted_review', label: 'Soumis' },
  { value: 'approved_secured', label: 'Sécurisé' },
  { value: 'rejected', label: 'Rejeté' },
  { value: 'suspended', label: 'Suspendu' },
  { value: 'cancelled', label: 'Annulé' }
];

const banks = [
  'Attijariwafa Bank', 'BMCE Bank', 'Crédit du Maroc', 'CIH Bank',
  'Banque Populaire', 'SGMB', 'CDM', 'Al Barid Bank'
];

const paymentModes = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
  { value: 'materiel_equipement', label: 'Matériel/Équipement' }
];

// @ts-nocheck
export default function FundingSourceForm({ isOpen, onClose, onSubmit, source, projectCurrency }) {
  const [showEncaissementForm, setShowEncaissementForm] = React.useState(false);

  const { register, handleSubmit, control, watch, formState: { errors }, reset } = useForm({
    defaultValues: source || {
      source_type: 'own_funds',
      amount: '',
      status: 'draft_preparation',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      encaissements: []
    }
  });

  const watchedType = watch('source_type');
  const watchedStatus = watch('status');
  const currentEncaissements = watch('encaissements') || [];

  React.useEffect(() => {
    if (source) {
      reset(source);
    }
  }, [source, reset]);

  const formatCurrency = (amount) => {
    const symbols = { EUR: '€', USD: '$', MAD: 'DH' };
    const symbol = symbols[projectCurrency] || projectCurrency;
    return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + symbol;
  };

  const getTotalEncaisse = () => {
    return currentEncaissements.reduce((sum, enc) => sum + (enc.amount || 0), 0);
  };

  const handleAddEncaissement = (encaissementData) => {
    const updatedEncaissements = [...currentEncaissements, encaissementData];
    const formData = watch();
    onSubmit({ ...formData, encaissements: updatedEncaissements });
    setShowEncaissementForm(false);
  };

  const handleDeleteEncaissement = (encaissementId) => {
    if (window.confirm('Supprimer cet encaissement ?')) {
      const updatedEncaissements = currentEncaissements.filter(enc => enc.id !== encaissementId);
      const formData = watch();
      onSubmit({ ...formData, encaissements: updatedEncaissements });
    }
  };

  const onSubmitForm = (data) => {
    onSubmit({ ...data, encaissements: currentEncaissements });
  };

  const totalEncaisse = getTotalEncaisse();
  const sourceAmount = watch('amount') || 0;
  const progressionPct = sourceAmount > 0 ? Math.min((totalEncaisse / sourceAmount) * 100, 100) : 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {source ? 'Modifier' : 'Ajouter'} une source de financement
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de source</Label>
                <Controller
                  name="source_type"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sourceTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div>
                <Label>Montant ({projectCurrency})</Label>
                <Input
                  {...register("amount", {
                    required: "Montant obligatoire",
                    valueAsNumber: true,
                    min: { value: 0.01, message: "Montant doit être positif" }
                  })}
                  type="number"
                  step="0.01"
                />
                {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message}</p>}
              </div>

              <div>
                <Label>Statut</Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            {/* Champs conditionnels selon le type */}
            {watchedType === 'bank_loan' && (
              <div className="space-y-4 p-4 border rounded-md bg-blue-50">
                <h4 className="font-semibold">Détails du financement bancaire</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name="bank_name"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une banque" />
                        </SelectTrigger>
                        <SelectContent>
                          {banks.map(bank => (
                            <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <Input placeholder="Type de prêt" {...register("loan_type")} />
                  <Input placeholder="Taux d'intérêt (%)" type="number" step="0.01" {...register("interest_rate", { valueAsNumber: true })} />
                  <Input placeholder="Type de garantie" {...register("guarantee_type")} />
                </div>
              </div>
            )}

            {watchedType === 'public_subsidy' && (
              <div className="space-y-4 p-4 border rounded-md bg-green-50">
                <h4 className="font-semibold">Détails de la subvention</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Organisme" {...register("subsidy_organisme")} />
                  <Input placeholder="Programme/Dispositif" {...register("subsidy_program")} />
                  <Input type="date" placeholder="Date limite soumission" {...register("submission_deadline")} />
                </div>
              </div>
            )}

            {watchedType === 'leasing' && (
              <div className="space-y-4 p-4 border rounded-md bg-purple-50">
                <h4 className="font-semibold">Détails du leasing</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Société de leasing" {...register("leasing_company")} />
                  <Input type="number" placeholder="Durée (mois)" {...register("leasing_duration", { valueAsNumber: true })} />
                  <Input placeholder="Catégorie d'équipement" {...register("equipment_category")} />
                  <div className="flex items-center gap-2 pt-6">
                    <input type="checkbox" id="buyout" {...register("buyout_option")} />
                    <Label htmlFor="buyout">Option d'achat</Label>
                  </div>
                </div>
              </div>
            )}

            {/* Informations de contact */}
            <div className="space-y-4 p-4 border rounded-md">
              <h4 className="font-semibold">Contact</h4>
              <div className="grid grid-cols-3 gap-4">
                <Input placeholder="Nom du contact" {...register("contact_person")} />
                <Input placeholder="Email" type="email" {...register("contact_email")} />
                <Input placeholder="Téléphone" {...register("contact_phone")} />
              </div>
            </div>

            {/* Section des encaissements (uniquement si sécurisé) */}
            {watchedStatus === 'approved_secured' && (
              <div className="space-y-4 p-4 border rounded-md bg-gray-50">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold">Historique des encaissements</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEncaissementForm(true)}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Déclarer un encaissement
                  </Button>
                </div>

                {currentEncaissements.length > 0 ? (
                  <>
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Total encaissé : {formatCurrency(totalEncaisse)}</span>
                        <span>Progression : {progressionPct.toFixed(1)}%</span>
                      </div>
                      <Progress value={progressionPct} className="h-2" />
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Mode</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Document</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentEncaissements.map((enc) => (
                          <TableRow key={enc.id}>
                            <TableCell>{new Date(enc.date).toLocaleDateString('fr-FR')}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {paymentModes.find(m => m.value === enc.payment_mode)?.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{formatCurrency(enc.amount)}</TableCell>
                            <TableCell>
                              {enc.document_url ? (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={enc.document_url} target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4" />
                                  </a>
                                </Button>
                              ) : (
                                <span className="text-gray-400 text-sm">Aucun</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEncaissement(enc.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucun encaissement déclaré</p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {source ? 'Mettre à jour' : 'Ajouter'} la source
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {showEncaissementForm && (
        <EncaissementForm
          isOpen={showEncaissementForm}
          onClose={() => setShowEncaissementForm(false)}
          onSubmit={handleAddEncaissement}
          projectCurrency={projectCurrency}
          maxAmount={sourceAmount - totalEncaisse}
        />
      )}
    </>
  );
}
